import type { Article } from "../insights";
import { ctaPl, ctaEn, ctaZh } from "../insight-cta";

export const rekrutacjaItKrakow: Article = {
  slug: "rekrutacja-it-krakow",
  updated: "2026-08-11",
  updatedLabel: {
    pl: "Aktualizacja: 11 sierpnia 2026",
    en: "Last updated: 11 August 2026",
    zh: "最近更新：2026 年 8 月 11 日",
  },
  topic: { pl: "Kraków", en: "Kraków", zh: "克拉科夫" },
  pl: {
    title: "Rekrutacja IT w Krakowie: rynek, widełki, czas obsadzenia",
    h1: "Rekrutacja IT w Krakowie 2026: rynek, widełki i czas obsadzenia",
    metaTitle: "Rekrutacja IT Kraków 2026: widełki, pula kandydatów, czas",
    metaDescription:
      "Rekrutacja IT w Krakowie 2026: ile płaci się seniorom na UoP i B2B, jak duża jest lokalna pula kandydatów, ile trwa obsadzenie roli i czym Kraków różni się od Warszawy.",
    eyebrow: "Insights · Kraków",
    summary:
      "Lokalny odczyt rynku: widełki krakowskie vs. Warszawa, wielkość puli kandydatów, model pracy i realny czas do oferty.",
    lead:
      "Kraków to drugi co do wielkości ośrodek IT w Polsce i nasz dom - prowadzimy stąd rekrutację IT na całą Polskę i region CEE. Poniżej opisujemy to, co widzimy w krakowskich procesach w 2026: kto realnie jest dostępny, ile kosztuje, jak wygląda konkurencja o kandydata i gdzie proces się wykłada.",
    note:
      "Kwoty miesięczne w PLN. UoP to brutto (koszt pracodawcy ok. +20%), B2B to wartość faktury netto bez VAT. Liczby to zakresy planistyczne z naszych procesów 2025-2026.",
    readTime: "8 min czytania",
    takeaways: [
      "Kraków to ok. 80 tys. specjalistów IT - drugi rynek w Polsce po Warszawie.",
      "Widełki krakowskie są zwykle 5-10% niżej niż warszawskie przy tym samym poziomie roli.",
      "Senior software engineer w Krakowie: 17 000 - 26 000 PLN brutto na UoP lub 21 000 - 32 000 PLN netto na B2B.",
      "Ponad 60% ofert w Krakowie to hybryda 2 dni w biurze; wymóg 5 dni w biurze zawęża pulę o ok. 50-60%.",
      "Największym pracodawcą IT w mieście są centra usług wspólnych i R&D globalnych firm - to one wyznaczają standard świadczeń pozapłacowych.",
      "Realny czas od rozmowy wprowadzającej do zaakceptowanej oferty w Krakowie: 3-5 tygodni przy sprawnym procesie decyzyjnym.",
    ],
    sections: [
      {
        id: "rynek",
        eyebrow: "01 / Rynek",
        title: "Jak wygląda krakowski rynek IT w 2026",
        blocks: [
          {
            kind: "table",
            caption: "Kraków na tle innych polskich hubów IT, 2026",
            head: ["Hub", "Szacowana pula IT", "Poziom widełek", "Charakter rynku"],
            rows: [
              ["Warszawa", "ok. 120 tys.", "Benchmark (100%)", "Fintech, korporacje, scale-upy"],
              ["Kraków", "ok. 80 tys.", "95 - 100%", "R&D globalnych firm, SSC/GBS, gaming"],
              ["Wrocław", "ok. 60 tys.", "92 - 98%", "Software house'y, automotive, cloud"],
              ["Trójmiasto", "ok. 40 tys.", "92 - 98%", "Embedded, produkty SaaS, biotech IT"],
              ["Poznań", "ok. 35 tys.", "90 - 95%", "E-commerce, logistyka, ERP"],
              ["Zdalnie (cała Polska)", "-", "85 - 95%", "Największa pula, największa konkurencja o uwagę"],
            ],
          },
          {
            kind: "p",
            text: "Kraków ma jedną cechę, która realnie zmienia rekrutację: bardzo dużą liczbę centrów R&D i SSC globalnych firm. Oznacza to stabilny standard świadczeń pozapłacowych (prywatna opieka, budżet szkoleniowy, elastyczne godziny) i kandydatów przyzwyczajonych do uporządkowanego procesu. Startup, który rekrutuje tu chaotycznie, przegrywa nie budżetem, tylko wrażeniem nieprzewidywalności.",
          },
        ],
      },
      {
        id: "widelki",
        eyebrow: "02 / Widełki",
        title: "Ile kosztuje inżynier w Krakowie",
        blocks: [
          {
            kind: "table",
            caption: "Widełki wynagrodzeń w Krakowie, 2026",
            head: ["Rola", "UoP (brutto/mc)", "B2B (netto/mc)", "Różnica vs. Warszawa"],
            rows: [
              ["Mid software engineer", "11 500 - 17 500 PLN", "13 500 - 21 000 PLN", "-5%"],
              ["Senior software engineer", "17 000 - 26 000 PLN", "21 000 - 32 000 PLN", "-5 do -8%"],
              ["Senior DevOps / SRE", "21 000 - 29 000 PLN", "25 000 - 34 000 PLN", "-5%"],
              ["Senior data engineer", "20 000 - 28 000 PLN", "24 000 - 33 000 PLN", "-5 do -10%"],
              ["ML / GenAI engineer (senior)", "23 000 - 33 000 PLN", "27 000 - 38 000 PLN", "-3 do -5%"],
              ["Engineering manager", "24 000 - 34 000 PLN", "28 000 - 40 000 PLN", "-8 do -10%"],
            ],
          },
          {
            kind: "p",
            text: "Różnica wobec Warszawy topnieje wraz z rzadkością profilu. Przy rolach AI/ML i platformowych rynek jest praktycznie ogólnopolski - kandydat porównuje ofertę z warszawską i zdalną tego samego dnia, więc argument „u nas w Krakowie płaci się mniej” nie działa.",
          },
        ],
      },
      {
        id: "model-pracy",
        eyebrow: "03 / Model pracy",
        title: "Biuro, hybryda, zdalnie - co realnie działa w Krakowie",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "Hybryda 2 dni w biurze",
                meta: "Ponad 60% naszych krakowskich procesów",
                body: "Domyślny standard rynkowy. Nie zawęża puli i pozwala dowozić onboarding oraz pracę zespołową. Kandydaci akceptują ją bez negocjowania podwyżki.",
              },
              {
                title: "Pełne biuro 5 dni",
                meta: "Pula mniejsza o 50-60%",
                body: "Działa tylko przy hardware, embedded, danych wrażliwych lub bardzo silnej marce. Jeśli musisz, dołóż realny powód i 8-12% do widełek.",
              },
              {
                title: "Hybryda elastyczna (1 dzień/mies.)",
                meta: "Kompromis dla ról seniorskich",
                body: "Pozwala rekrutować z całej południowej Polski - Katowice, Rzeszów, Kielce są w zasięgu jednego dojazdu w miesiącu.",
              },
              {
                title: "Pełne zdalne",
                meta: "Największa pula, najwięcej konkurencji",
                body: "Otwiera cały kraj, ale wtedy konkurujesz również z ofertami zagranicznymi w EUR. Wygrywa jasny zakres odpowiedzialności i szybka decyzja, nie sam pakiet.",
              },
            ],
          },
        ],
      },
      {
        id: "proces",
        eyebrow: "04 / Proces",
        title: "Ile trwa obsadzenie roli w Krakowie",
        blocks: [
          {
            kind: "table",
            caption: "Typowy harmonogram rekrutacji IT w Krakowie",
            head: ["Etap", "Czas", "Uwagi"],
            rows: [
              ["Brief i mapa rynku", "2 - 3 dni", "Kalibracja widełek i realnej puli kandydatów"],
              ["Dotarcie i rozmowy wstępne", "5 - 10 dni", "Kandydaci pasywni, wiadomości z widełkami"],
              ["Krótka lista dla klienta", "do 14 dni od rozmowy wprowadzającej", "4 - 6 profili gotowych do decyzji"],
              ["Proces po stronie klienta", "7 - 14 dni", "Maks. 2-3 spotkania, inaczej tracisz finalistów"],
              ["Oferta i decyzja kandydata", "3 - 7 dni", "Kandydat zwykle ma 2-3 procesy równolegle"],
              ["Okres wypowiedzenia", "1 - 3 mies. (UoP), 2 - 4 tyg. (B2B)", "B2B skraca start średnio o miesiąc"],
            ],
          },
          {
            kind: "p",
            text: "Najczęstszy powód porażki w Krakowie to nie budżet, tylko rytm decyzji. Jeśli między ostatnią rozmową a ofertą mija więcej niż 10 dni roboczych, tracisz około połowy finalistów - w mieście, w którym kandydat ma alternatywę w tym samym tygodniu, tempo jest częścią oferty.",
          },
        ],
      },
    ],
    faqTitle: "Najczęstsze pytania: rekrutacja IT w Krakowie",
    faq: [
      {
        q: "Ile trwa rekrutacja IT w Krakowie?",
        a: "Od rozmowy wprowadzającej do zaakceptowanej oferty zwykle 3-5 tygodni przy sprawnym procesie po stronie klienta. Krótką listę gotową do decyzji dowozimy w 14 dni w ramach sprintu Fit-to-Win.",
      },
      {
        q: "Czy w Krakowie płaci się mniej niż w Warszawie?",
        a: "Zwykle 5-10% mniej przy tym samym poziomie roli, ale różnica maleje przy rzadkich profilach (AI/ML, platform, security), gdzie rynek jest ogólnopolski.",
      },
      {
        q: "Jak duża jest pula kandydatów IT w Krakowie?",
        a: "Około 80 tys. specjalistów IT - to drugi rynek w Polsce. Dla konkretnego zestawie technologii i seniority realna pula to zwykle kilkaset osób, dlatego mapa rynku ma sens przed startem.",
      },
      {
        q: "Czy wymóg pracy z biura w Krakowie zawęża rekrutację?",
        a: "Tak. Pięć dni w biurze zmniejsza dostępną pulę o około 50-60% wobec hybrydy dwudniowej, która jest dziś standardem rynkowym.",
      },
    ],
    cta: ctaPl,
    methodology:
      "Zakresy pochodzą z procesów rekrutacyjnych Positive Recruitment prowadzonych w Krakowie w latach 2025-2026 i są konfrontowane z publicznymi raportami płacowymi polskiego IT. Wielkości puli to szacunki rynkowe, nie dane urzędowe.",
  },
  en: {
    title: "IT recruitment in Kraków: market, salaries, time to hire",
    h1: "IT recruitment in Kraków 2026: market, salaries and time to hire",
    metaTitle: "IT recruitment Kraków 2026: salaries, talent pool, timelines",
    metaDescription:
      "IT recruitment in Kraków 2026: what senior engineers cost on employment and B2B, how deep the local talent pool is, how long a hire takes and how Kraków compares to Warsaw.",
    eyebrow: "Insights · Kraków",
    summary:
      "A local market read: Kraków versus Warsaw pay, talent pool depth, work models and realistic time to offer.",
    lead:
      "Kraków is Poland's second largest tech hub and our home base - we run IT recruitment from here across Poland and the CEE region. Below is what we actually see in Kraków processes in 2026: who is available, what they cost, how competitive the market is and where hiring processes break.",
    note:
      "All amounts are monthly in PLN. Employment (UoP) figures are gross (employer cost roughly +20%); B2B figures are net invoice value excluding VAT. Numbers are planning ranges from our 2025-2026 processes.",
    readTime: "8 min read",
    takeaways: [
      "Kraków hosts roughly 80,000 IT professionals - Poland's second largest tech market after Warsaw.",
      "Kraków salary ranges typically sit 5-10% below Warsaw for the same role level.",
      "A senior software engineer in Kraków costs 17,000-26,000 PLN gross on employment or 21,000-32,000 PLN net on B2B.",
      "Over 60% of Kraków roles are hybrid with two office days; a five-day office requirement cuts the pool by 50-60%.",
      "Global R&D centres and shared service centres are the biggest local employers and set the benefits benchmark.",
      "Realistic time from brief to accepted offer in Kraków: 3-5 weeks with a decisive client process.",
    ],
    sections: [
      {
        id: "rynek",
        eyebrow: "01 / Market",
        title: "What the Kraków tech market looks like in 2026",
        blocks: [
          {
            kind: "table",
            caption: "Kraków compared with other Polish tech hubs, 2026",
            head: ["Hub", "Estimated IT pool", "Pay level", "Market character"],
            rows: [
              ["Warsaw", "approx. 120,000", "Benchmark (100%)", "Fintech, corporates, scale-ups"],
              ["Kraków", "approx. 80,000", "95 - 100%", "Global R&D, SSC/GBS, gaming"],
              ["Wroclaw", "approx. 60,000", "92 - 98%", "Software houses, automotive, cloud"],
              ["Tricity", "approx. 40,000", "92 - 98%", "Embedded, SaaS products, biotech IT"],
              ["Poznan", "approx. 35,000", "90 - 95%", "E-commerce, logistics, ERP"],
              ["Remote (all Poland)", "-", "85 - 95%", "Largest pool, hardest fight for attention"],
            ],
          },
          {
            kind: "p",
            text: "One Kraków feature genuinely changes hiring: the density of global R&D and shared service centres. That means a stable benefits benchmark (private healthcare, learning budget, flexible hours) and candidates used to a structured process. A startup that hires chaotically here loses on perceived unpredictability, not on budget.",
          },
        ],
      },
      {
        id: "widelki",
        eyebrow: "02 / Pay",
        title: "What an engineer costs in Kraków",
        blocks: [
          {
            kind: "table",
            caption: "Kraków salary ranges, 2026",
            head: ["Role", "Employment (gross/mo)", "B2B (net/mo)", "Delta vs Warsaw"],
            rows: [
              ["Mid software engineer", "11,500 - 17,500 PLN", "13,500 - 21,000 PLN", "-5%"],
              ["Senior software engineer", "17,000 - 26,000 PLN", "21,000 - 32,000 PLN", "-5 to -8%"],
              ["Senior DevOps / SRE", "21,000 - 29,000 PLN", "25,000 - 34,000 PLN", "-5%"],
              ["Senior data engineer", "20,000 - 28,000 PLN", "24,000 - 33,000 PLN", "-5 to -10%"],
              ["ML / GenAI engineer (senior)", "23,000 - 33,000 PLN", "27,000 - 38,000 PLN", "-3 to -5%"],
              ["Engineering manager", "24,000 - 34,000 PLN", "28,000 - 40,000 PLN", "-8 to -10%"],
            ],
          },
          {
            kind: "p",
            text: "The Warsaw gap shrinks as the profile gets rarer. For AI/ML and platform roles the market is effectively national - a candidate compares your offer with a Warsaw and a remote one on the same day, so \"Kraków pays less\" is not an argument that survives contact with the market.",
          },
        ],
      },
      {
        id: "model-pracy",
        eyebrow: "03 / Work model",
        title: "Office, hybrid or remote - what actually works in Kraków",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "Hybrid, two office days",
                meta: "Over 60% of our Kraków processes",
                body: "The default market standard. It does not shrink the pool and still supports onboarding and team work. Candidates accept it without asking for a premium.",
              },
              {
                title: "Full office, five days",
                meta: "Pool 50-60% smaller",
                body: "Only works for hardware, embedded, sensitive data or a very strong brand. If you must, give a real reason and add 8-12% to the range.",
              },
              {
                title: "Flexible hybrid (one day a month)",
                meta: "A compromise for senior roles",
                body: "Opens up all of southern Poland - Katowice, Rzeszow and Kielce are within a single monthly commute.",
              },
              {
                title: "Fully remote",
                meta: "Biggest pool, most competition",
                body: "Opens the whole country, but you now compete with EUR-denominated offers too. Clear ownership and a fast decision win here, not the package alone.",
              },
            ],
          },
        ],
      },
      {
        id: "proces",
        eyebrow: "04 / Process",
        title: "How long a Kraków hire really takes",
        blocks: [
          {
            kind: "table",
            caption: "Typical IT hiring timeline in Kraków",
            head: ["Stage", "Duration", "Notes"],
            rows: [
              ["Brief and market map", "2 - 3 days", "Calibrating the range and the real talent pool"],
              ["Outreach and screening calls", "5 - 10 days", "Passive candidates, messages with pay ranges"],
              ["Shortlist to the client", "within 14 days of brief", "4 - 6 decision-ready profiles"],
              ["Client-side process", "7 - 14 days", "Two or three meetings max, otherwise finalists drop"],
              ["Offer and candidate decision", "3 - 7 days", "Candidates usually run 2-3 processes in parallel"],
              ["Notice period", "1 - 3 months (employment), 2 - 4 weeks (B2B)", "B2B shortens the start by about a month"],
            ],
          },
          {
            kind: "p",
            text: "The most common reason a Kraków hire fails is decision rhythm, not budget. If more than 10 working days pass between the last interview and the offer, you lose roughly half the finalists - in a city where a candidate has an alternative the same week, speed is part of the offer.",
          },
        ],
      },
    ],
    faqTitle: "FAQ: IT recruitment in Kraków",
    faq: [
      {
        q: "How long does IT recruitment in Kraków take?",
        a: "Typically 3-5 weeks from brief to accepted offer with a decisive client process. Our Fit-to-Win Sprint delivers a decision-ready shortlist within 14 days.",
      },
      {
        q: "Does Kraków pay less than Warsaw?",
        a: "Usually 5-10% less for the same role level, but the gap narrows for rare profiles (AI/ML, platform, security) where the market is national rather than local.",
      },
      {
        q: "How big is the Kraków IT talent pool?",
        a: "Around 80,000 IT professionals, Poland's second largest market. For a specific stack and seniority the realistic pool is usually a few hundred people, which is why a market map before launch pays off.",
      },
      {
        q: "Does requiring office work in Kraków narrow the search?",
        a: "Yes. Five office days cut the available pool by roughly 50-60% compared with the two-day hybrid that is today's market standard.",
      },
    ],
    cta: ctaEn,
    methodology:
      "Ranges come from Positive Recruitment processes run in Kraków during 2025-2026 and are cross-checked against public Polish IT salary reports. Pool sizes are market estimates, not official statistics.",
  },
  zh: {
    title: "克拉科夫IT招聘：市场、薪酬与招聘周期",
    h1: "2026年克拉科夫IT招聘：市场、薪酬与招聘周期",
    metaTitle: "克拉科夫IT招聘2026：薪酬、人才池与周期",
    metaDescription:
      "2026年克拉科夫IT招聘指南：高级工程师在雇佣合同与B2B下的成本、本地人才池规模、招聘所需时间，以及克拉科夫与华沙的差异。",
    eyebrow: "洞察 · 克拉科夫",
    summary: "本地市场判断：克拉科夫与华沙的薪酬差异、人才池深度、工作模式与实际发offer周期。",
    lead:
      "克拉科夫是波兰第二大科技中心，也是我们的大本营——我们从这里为波兰全境和中东欧地区提供IT招聘服务。以下是我们在2026年克拉科夫招聘流程中真实看到的情况：谁可被吸引、成本多少、竞争有多激烈，以及流程通常在哪里失败。",
    note:
      "金额均为每月、以波兰兹罗提（PLN）计。雇佣合同（UoP）为税前工资（雇主成本约再加20%）,B2B为不含增值税的净发票金额。数据为我们2025-2026年流程的规划区间。",
    readTime: "阅读约 8 分钟",
    takeaways: [
      "克拉科夫约有8万名IT专业人士，是仅次于华沙的波兰第二大科技市场。",
      "同级别岗位，克拉科夫的薪酬区间通常比华沙低5%-10%。",
      "克拉科夫高级软件工程师：雇佣合同17,000-26,000 PLN税前，或B2B 21,000-32,000 PLN净额。",
      "超过60%的克拉科夫岗位是每周到岗2天的混合办公；要求每周5天到岗会使人才池缩小50%-60%。",
      "全球研发中心和共享服务中心是本地最大雇主，并设定了福利基准。",
      "在客户决策高效的前提下，从简报到接受offer的实际周期为3-5周。",
    ],
    sections: [
      {
        id: "rynek",
        eyebrow: "01 / 市场",
        title: "2026年克拉科夫科技市场概况",
        blocks: [
          {
            kind: "table",
            caption: "克拉科夫与波兰其他科技中心对比,2026",
            head: ["城市", "IT人才池估算", "薪酬水平", "市场特征"],
            rows: [
              ["华沙", "约 120,000", "基准 (100%)", "金融科技、大型企业、成长型公司"],
              ["克拉科夫", "约 80,000", "95% - 100%", "全球研发中心、共享服务中心、游戏"],
              ["弗罗茨瓦夫", "约 60,000", "92% - 98%", "软件公司、汽车、云"],
              ["三联城", "约 40,000", "92% - 98%", "嵌入式、SaaS产品、生物科技IT"],
              ["波兹南", "约 35,000", "90% - 95%", "电商、物流、ERP"],
              ["远程（波兰全境）", "-", "85% - 95%", "人才池最大，注意力竞争也最激烈"],
            ],
          },
          {
            kind: "p",
            text: "克拉科夫有一个真正影响招聘的特点：全球研发中心和共享服务中心非常密集。这意味着稳定的福利基准（私人医疗、学习预算、弹性工时）以及习惯了规范流程的候选人。在这里流程混乱的初创公司，输的不是预算，而是给人的不可预测感。",
          },
        ],
      },
      {
        id: "widelki",
        eyebrow: "02 / 薪酬",
        title: "在克拉科夫雇一名工程师要多少钱",
        blocks: [
          {
            kind: "table",
            caption: "克拉科夫薪酬区间,2026",
            head: ["岗位", "雇佣合同（税前/月）", "B2B（净额/月）", "与华沙的差异"],
            rows: [
              ["中级软件工程师", "11,500 - 17,500 PLN", "13,500 - 21,000 PLN", "-5%"],
              ["高级软件工程师", "17,000 - 26,000 PLN", "21,000 - 32,000 PLN", "-5% 至 -8%"],
              ["高级 DevOps / SRE", "21,000 - 29,000 PLN", "25,000 - 34,000 PLN", "-5%"],
              ["高级数据工程师", "20,000 - 28,000 PLN", "24,000 - 33,000 PLN", "-5% 至 -10%"],
              ["ML / GenAI 工程师（高级）", "23,000 - 33,000 PLN", "27,000 - 38,000 PLN", "-3% 至 -5%"],
              ["工程经理", "24,000 - 34,000 PLN", "28,000 - 40,000 PLN", "-8% 至 -10%"],
            ],
          },
          {
            kind: "p",
            text: "岗位越稀缺，与华沙的差距就越小。在AI/ML和平台类岗位上，市场实际上是全国性的——候选人会在同一天比较你的offer、华沙的offer和远程offer,因此“克拉科夫本来就给得少”这个理由站不住脚。",
          },
        ],
      },
      {
        id: "model-pracy",
        eyebrow: "03 / 工作模式",
        title: "坐班、混合还是远程——在克拉科夫什么真正有效",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "混合办公，每周到岗2天",
                meta: "占我们克拉科夫流程的60%以上",
                body: "当前的市场默认标准。既不缩小人才池，又能支撑入职培养和团队协作。候选人通常不会因此要求加薪。",
              },
              {
                title: "每周5天全坐班",
                meta: "人才池缩小50%-60%",
                body: "只适用于硬件、嵌入式、敏感数据或品牌非常强势的情况。如果必须如此，请给出真实理由并把薪资区间上调8%-12%。",
              },
              {
                title: "弹性混合（每月1天）",
                meta: "适合高级岗位的折中方案",
                body: "可以覆盖波兰南部全境——卡托维兹、热舒夫和凯尔采都在每月一次通勤的范围内。",
              },
              {
                title: "完全远程",
                meta: "人才池最大，竞争也最激烈",
                body: "面向全国开放，但同时也要和以欧元计价的offer竞争。这时取胜的是清晰的职责边界和快速决策，而不仅是薪酬包。",
              },
            ],
          },
        ],
      },
      {
        id: "proces",
        eyebrow: "04 / 流程",
        title: "在克拉科夫完成一次招聘需要多久",
        blocks: [
          {
            kind: "table",
            caption: "克拉科夫典型IT招聘时间表",
            head: ["阶段", "时长", "说明"],
            rows: [
              ["简报与市场地图", "2 - 3 天", "校准薪酬区间和真实人才池"],
              ["触达与初步沟通", "5 - 10 天", "被动候选人，信息中附薪资区间"],
              ["向客户提交候选名单", "简报后14天内", "4 - 6 位可直接决策的候选人"],
              ["客户方流程", "7 - 14 天", "最多2-3轮面试，否则会流失决赛候选人"],
              ["发offer与候选人决策", "3 - 7 天", "候选人通常同时推进2-3个流程"],
              ["通知期", "1 - 3 个月（雇佣合同）,2 - 4 周（B2B）", "B2B平均能把到岗时间提前约一个月"],
            ],
          },
          {
            kind: "p",
            text: "在克拉科夫，招聘失败最常见的原因是决策节奏而非预算。如果最后一轮面试到发offer之间超过10个工作日，大约会失去一半的决赛候选人——在候选人同一周就有备选机会的城市里，速度本身就是offer的一部分。",
          },
        ],
      },
    ],
    faqTitle: "常见问题：克拉科夫IT招聘",
    faq: [
      {
        q: "在克拉科夫招聘IT人才需要多久？",
        a: "在客户决策高效的情况下，从简报到接受offer通常为3-5周。我们的 Fit-to-Win Sprint 会在14天内交付可直接决策的候选名单。",
      },
      {
        q: "克拉科夫的薪酬比华沙低吗？",
        a: "同级别岗位通常低5%-10%,但在AI/ML、平台和安全等稀缺岗位上差距会明显缩小，因为这些岗位的市场是全国性的。",
      },
      {
        q: "克拉科夫的IT人才池有多大？",
        a: "约8万名IT专业人士，是波兰第二大市场。针对具体技术栈和级别，真实可触达的人数通常是几百人，因此启动前做市场地图很有价值。",
      },
      {
        q: "要求在克拉科夫坐班会缩小招聘范围吗？",
        a: "会。与当前市场标准的每周2天混合办公相比，每周5天坐班会使可触达人才池缩小约50%-60%。",
      },
    ],
    cta: ctaZh,
    methodology:
      "上述区间来自 Positive Recruitment 于2025-2026年在克拉科夫开展的招聘流程，并与波兰IT行业公开薪酬报告相互印证。人才池规模为市场估算，非官方统计数据。",
  },
};
